import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react'
import MermaidDiagram from '../components/MermaidDiagram'

// Safe URI decode — returns '' on failure rather than throwing
function safeDecodeURI(str) {
  if (!str) return ''
  try { return decodeURIComponent(str) } catch { return str }
}

// ── Table renderer (markdown table → visual) ──────────────────────────────────

function TableFigureView({ code, title, caption }) {
  // Convert simple markdown table to HTML table rows
  const renderMarkdownTable = (md) => {
    const lines = md.trim().split('\n').filter(l => l.trim() && !l.match(/^\s*[\-|]+\s*$/))
    if (lines.length === 0) return null
    const parseRow = (line) =>
      line.split('|').map(c => c.trim()).filter(Boolean)

    const [headerLine, ...bodyLines] = lines
    const headers = parseRow(headerLine)

    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{ background: '#ede8df', fontWeight: 600, border: '1px solid #c5b89a', padding: '0.4rem 0.65rem' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyLines.map((row, ri) => (
            <tr key={ri}>
              {parseRow(row).map((cell, ci) => (
                <td key={ci} style={{ border: '1px solid #c5b89a', padding: '0.4rem 0.65rem' }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  return (
    <figure style={{ margin: '0 0 1.5rem', padding: '1.5rem', background: '#f8f6f2', border: '1px solid #d9d0c3', borderRadius: 6 }}>
      {title && (
        <figcaption style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1e293b', marginBottom: '1rem', textAlign: 'center' }}>
          {title}
        </figcaption>
      )}
      <div style={{ overflowX: 'auto' }}>
        {renderMarkdownTable(code) || (
          <pre style={{ fontSize: '0.8rem', color: '#334155', margin: 0, textAlign: 'left', overflowX: 'auto' }}>{code}</pre>
        )}
      </div>
      {caption && (
        <figcaption style={{ fontStyle: 'italic', fontSize: '0.8rem', color: '#64748b', marginTop: '1rem', textAlign: 'center' }}>
          {caption}
        </figcaption>
      )}
    </figure>
  )
}

// ── SVG chart renderer (Research Lab figures) ─────────────────────────────────

function SVGChartFigureView({ code, title, caption }) {
  return (
    <figure style={{ margin: '0 0 1.5rem', padding: '1.25rem', background: '#f8f6f2', border: '1px solid #d9d0c3', borderRadius: 6, textAlign: 'center' }}>
      {title && (
        <figcaption style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1e293b', marginBottom: '1rem' }}>
          {title}
        </figcaption>
      )}
      <div
        style={{ display: 'inline-block', maxWidth: '100%', overflowX: 'auto' }}
        dangerouslySetInnerHTML={{ __html: code }}
      />
      {caption && (
        <figcaption style={{ fontStyle: 'italic', fontSize: '0.8rem', color: '#64748b', marginTop: '0.75rem' }}>
          {caption}
        </figcaption>
      )}
    </figure>
  )
}

// ── TipTap NodeView component ─────────────────────────────────────────────────

function ThesisFigureNodeView({ node, selected }) {
  const { figureType, code, title, caption, figureId } = node.attrs

  return (
    <NodeViewWrapper
      contentEditable={false}
      style={{
        outline: selected ? '2px solid rgba(37,99,235,0.5)' : 'none',
        outlineOffset: 4,
        borderRadius: 6,
        userSelect: 'none',
        cursor: 'default',
      }}
    >
      {!code ? (
        <div style={{
          padding: '1.25rem', background: '#f8f6f2', border: '1px dashed #d9d0c3',
          borderRadius: 6, textAlign: 'center', fontSize: '0.8rem', color: '#94a3b8',
        }}>
          [{figureId || 'Figure'}]
        </div>
      ) : figureType === 'chart-svg' ? (
        <SVGChartFigureView code={code} title={title} caption={caption} />
      ) : figureType === 'table' ? (
        <TableFigureView code={code} title={title} caption={caption} />
      ) : (
        <MermaidDiagram code={code} title={title} caption={caption} />
      )}
    </NodeViewWrapper>
  )
}

// ── TipTap Extension ──────────────────────────────────────────────────────────

export const ThesisFigureExtension = Node.create({
  name: 'thesisFigure',
  group: 'block',
  atom: true,      // single undivisible block — delete as a unit
  draggable: true, // can be repositioned by drag

  addAttributes() {
    return {
      figureId:   { default: '' },
      figureType: { default: 'mermaid' },
      code:       { default: '' },
      title:      { default: '' },
      caption:    { default: '' },
    }
  },

  // Parse from HTML (called when editor.setContent(html) is used)
  parseHTML() {
    return [{
      tag: 'div[data-thesis-figure]',
      getAttrs: (dom) => ({
        figureId:   dom.getAttribute('data-figure-id')      || '',
        figureType: dom.getAttribute('data-figure-type')    || 'mermaid',
        code:       safeDecodeURI(dom.getAttribute('data-figure-code')),
        title:      safeDecodeURI(dom.getAttribute('data-figure-title')),
        caption:    safeDecodeURI(dom.getAttribute('data-figure-caption')),
      }),
    }]
  },

  // Serialize to HTML (called by editor.getHTML() and on save)
  // Full figure data is embedded as URL-encoded attributes so content is
  // self-contained and survives save/load without a DB fetch.
  renderHTML({ node }) {
    const { figureId, figureType, code, title, caption } = node.attrs
    return ['div', mergeAttributes({
      'data-thesis-figure': 'true',
      'data-figure-id':      figureId   || '',
      'data-figure-type':    figureType || 'mermaid',
      'data-figure-code':    encodeURIComponent(code    || ''),
      'data-figure-title':   encodeURIComponent(title   || ''),
      'data-figure-caption': encodeURIComponent(caption || ''),
    })]
  },

  addNodeViews() {
    return { react: ReactNodeViewRenderer(ThesisFigureNodeView) }
  },
})
